<script setup lang="ts">
import { usePaymentMethods } from "~/composables/usePaymentMethods";
const { navigationElements } = useNavigation({ type: "footer-navigation" });
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { statistics, fetchReviewStatistics } = useReviewStatistics();
const {
  ratingData,
  bekanntData,
  personalAdviceData,
  getProductCustomSettings,
} = useProductPageCustomData();
onMounted(async () => {
  await getProductCustomSettings();
  await fetchReviewStatistics();
});

const averageRating = computed(() => {
  return statistics?.value?.data?.averageRating?.toFixed(2) || '....';
});

const { paymentMethodData, fetchPaymentMethods } = usePaymentMethods();
onMounted(() => {
  nextTick(() => fetchPaymentMethods());
});
</script>

<template>
  <section class="payment-information pt-12 md:pt-16">
    <LayoutFooterHorizontalLine />

    <section class="payment-information py-12 md:py-16">
      <div
        class="container flex flex-col flex-wrap md:flex-row justify-between items-start gap-8"
      >
        <!-- Summary section -->
        <div>
          <h3 class="text-lg font-normal text-primary mb-4 text-nowrap">
            {{ $t("footer.customerService") }}
          </h3>
          <div class="flex flex-col gap-4 3xl:gap-6 text-base 3xl:text-md">
            <div class="contact-wrap leading-7">
              <div class="contact-item block">
                {{ $t("companyDetails.phone") }}:
                <a
                  :href="`tel:${$t('companyDetails.contactNumber')}`"
                  class="font-bold hover:text-primary"
                  >{{ $t('companyDetails.contactNumber') }}</a
                >
              </div>
              <div class="contact-item block">
                {{ $t("companyDetails.emailText") }}:
                <NuxtLink
                  :to="`mailto:${$t('companyDetails.email')}`"
                  class="font-bold hover:text-primary"
                >
                  {{ $t('companyDetails.email') }}
                </NuxtLink>
              </div>
            </div>

            <div class="working-hours leading-7">
                <span class="block">{{
                  $t("companyDetails.timingsMonday")
                }}</span>
                <span class="block">{{
                  $t("companyDetails.timingsSaturday")
                }}</span>
            </div>
          </div>
        </div>

        <!-- Payment methods section -->
        <div>
          <h3
            class="text-lg font-normal text-primary mb-4 text-wrap sm:text-nowrap"
          >
            {{ $t("footer.paymentMethodsText") }}
          </h3>
          <LayoutFooterPaymentMethods :hide-heading="true" :payment-method-data="paymentMethodData" />
        </div>

        <div>
          <h3 class="text-lg font-normal text-primary mb-4 text-nowrap">
            {{ $t("footer.secureShoppingSection.header") }}
          </h3>
          <div class="flex items-center gap-4 flex-wrap">
            <div class="flex items-center gap-4 flex-wrap">
              <div class="flex gap-2 [&>img]:h-[44px]">
                <img
                  src="/assets/images/ssl-secure.png"
                  :alt="$t('footer.sslSecure')"
                  height="44"
                  width="44"
                  loading="lazy"
                  format="webp"
                />
                <NuxtImg
                  :src="ratingData?.ratingLogo?.url"
                  :alt="$t('footer.trustedShops')"
                  height="44"
                  width="44"
                  loading="lazy"
                  format="webp"
                />
              </div>
              <div class="flex flex-col gap-2 min-w-[96px]">
                <div
                  v-if="averageRating"
                  class="text-md font-medium leading-[18px] text-center"
                >
                  {{ averageRating }}/
                  <span class="text-gray-500 font-light">5.00</span>
                </div>
                <div class="flex items-center gap-x-0 sm:gap-x-[4px] text-[13px] sm:text-base text-rating-yellow relative -mt-[4px] sm:mt-0">
                  <template v-for="starIndex in 5" :key="starIndex">
                    <div class="relative inline-block">
                      <!-- Background star (always gray) -->
                      <i class="icon-star-sharp inline-block w-[13px] h-[13px] sm:w-[16px] sm:h-[16px] text-gray-300"></i>
                      <!-- Foreground star (filled portion) -->
                      <div 
                        class="absolute top-0 left-0 bottom-0 h-full overflow-hidden"
                        :style="{
                          width: averageRating ? `${Math.min(100, Math.max(0, ((parseFloat(averageRating) - (starIndex - 1)) * 100))) }%` : '0%'
                        }">
                        <i class="icon-star-sharp inline-block w-[13px] h-[13px] sm:w-[16px] sm:h-[16px] text-rating-yellow"></i>
                     </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <footer class="bg-primary/5 text-grey-700 mt-auto">
      <!-- Copyright -->
      <LayoutFooterCopyright />

      <!-- Back to top button -->
      <LayoutFooterBackToTop />
    </footer>
  </section>
</template>
